import { useEffect, useState } from "react";

import { AnswerType, BusinessInfoFlowProps } from "@/types/questionnaire";
import { trackBusinessStep, trackBusinessComplete } from "@/utils/analytics";

import { useBusinessQuestionnaireOptions } from "./useBusinessQuestionnaireOptions";
import BusinessInfoSteps from "./BusinessInfoSteps";
import BusinessInfoIntro from "./BusinessInfoIntro";

const STEP_NAMES = ["industry", "size", "revenue", "balance"] as const;

const extractAllValueIds = (answer?: AnswerType | null): number[] => {
	if (!answer?.values) {
		return [];
	}

	return answer.values.map((v) => v.id).filter((id): id is number => typeof id === "number");
};

const extractFirstValueId = (answer?: AnswerType | null): number | null => {
	return typeof answer?.values?.[0]?.id === "number" ? answer.values[0].id : null;
};

export default function BusinessInfoFlow({ onComplete }: BusinessInfoFlowProps) {
	const [showIntro, setShowIntro] = useState(true);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [answers, setAnswer] = useState<(AnswerType | null)[]>([]);

	const { industries, companySizes, companyRevenues, companyBalances, isLoading } =
		useBusinessQuestionnaireOptions();

	// Track step changes
	useEffect(() => {
		if (!showIntro) {
			trackBusinessStep(currentStepIndex + 1, STEP_NAMES[currentStepIndex]);
		}
	}, [currentStepIndex, showIntro]);

	if (showIntro) {
		return <BusinessInfoIntro onContinue={() => setShowIntro(false)} />;
	}

	return (
		<BusinessInfoSteps
			companyBalances={companyBalances.filter(
				(b): b is { id: number; value: string } => typeof b.id === "number"
			)}
			companyRevenues={companyRevenues.filter(
				(r): r is { id: number; value: string } => typeof r.id === "number"
			)}
			companySizes={companySizes.filter(
				(s): s is { id: number; value: string } => typeof s.id === "number"
			)}
			currentAnswers={answers.filter((a): a is AnswerType => a !== null)}
			currentStepIndex={currentStepIndex}
			handleBack={(updatedAnswersOrNull) => {
				if (updatedAnswersOrNull) {
					setAnswer(updatedAnswersOrNull);
				}

				setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
			}}
			handleNext={(updatedAnswers) => {
				setAnswer(updatedAnswers);

				if (currentStepIndex !== 3) {
					setCurrentStepIndex((prev) => prev + 1);

					return;
				}

				const [industryAnswer, sizeAnswer, revenueAnswer, balanceAnswer] = updatedAnswers;

				const industryIds = extractAllValueIds(industryAnswer);
				const companySizeId = extractFirstValueId(sizeAnswer);
				const companyRevenueId = extractFirstValueId(revenueAnswer);
				const companyBalanceId = extractFirstValueId(balanceAnswer);

				if (
					industryIds.length > 0 &&
					companySizeId !== null &&
					companyRevenueId !== null &&
					companyBalanceId !== null
				) {
					// Track business info completion
					trackBusinessComplete();
					onComplete({
						industryIds,
						companySizeId,
						companyRevenueId,
						companyBalanceId,
					});
				}
			}}
			industries={industries.filter(
				(i): i is { id: number; name: string } => typeof i.id === "number"
			)}
			isLoading={isLoading}
		/>
	);
}
